'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function LoveCalculator() {
  const t = useTranslations("tools.loveCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [inputs, setInputs] = useState({
    name1: '',
    name2: ''
  });
  const [results, setResults] = useState<{
    score: number;
    message: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const calculateLoveScore = (name1: string, name2: string): number => {
    const combined = (name1 + name2).toLowerCase().replace(/\s/g, '');
    let hash = 0;

    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const score = Math.abs(hash % 101);
    return score;
  };

  const getScoreMessage = (score: number) => {
    if (score >= 90) {
      return {
        message: t('scorePerfect'),
        description: t('scorePerfectDesc')
      };
    } else if (score >= 70) {
      return {
        message: t('scoreGreat'),
        description: t('scoreGreatDesc')
      };
    } else if (score >= 50) {
      return {
        message: t('scoreGood'),
        description: t('scoreGoodDesc')
      };
    } else if (score >= 30) {
      return {
        message: t('scoreModerate'),
        description: t('scoreModerateDesc')
      };
    } else {
      return {
        message: t('scoreChallenging'),
        description: t('scoreChallengingDesc')
      };
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputs.name1.trim() || !inputs.name2.trim()) {
      return;
    }

    const score = calculateLoveScore(inputs.name1, inputs.name2);
    const messageData = getScoreMessage(score);

    setResults({
      score,
      message: messageData.message,
      description: messageData.description
    });

    setTimeout(() => {
      const resultsElement = document.getElementById('results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const colors = {
    primary: '#ec4899',
    primaryDark: '#db2777',
    secondary: '#f9a8d4',
    accent: '#fce7f3',
    textPrimary: '#1f2937',
    textSecondary: '#374151',
    warning: '#f59e0b',
    warningBg: '#fef3c7',
    warningText: '#92400e',
    resultBg: '#fef3c7',
    resultText: '#7c2d12',
    scorePrimary: '#dc2626',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '90%' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 50%, #f9a8d4 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 20px 60px rgba(219, 39, 119, 0.15)',
      overflow: 'hidden' as const
    },
    { maxWidth: '100%' },
    { maxWidth: '600px', borderRadius: '1.25rem' },
    { maxWidth: '800px', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '2rem 1.5rem' },
    { padding: '2.25rem 2rem' },
    { padding: '2.5rem 2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    {
      margin: 0,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolHeaderPStyle = getResponsiveStyle(
    {
      margin: '0.75rem 0 0 0',
      opacity: 0.95
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '2rem 1.5rem' },
    { padding: '2.25rem 2rem' },
    { padding: '2.5rem 2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    {
      display: 'grid',
      alignItems: 'end' as const,
      marginBottom: '2rem'
    },
    { gridTemplateColumns: '1fr', gap: '1rem' },
    { gridTemplateColumns: '1fr auto 1fr', gap: '1.25rem' },
    { gridTemplateColumns: '1fr auto 1fr', gap: '1.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      textAlign: 'center' as const
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      border: '2px solid #e5e7eb',
      borderRadius: '0.75rem',
      transition: 'all 0.3s ease',
      textAlign: 'center' as const,
      fontWeight: 500
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const heartIconStyle = getResponsiveStyle(
    {
      color: colors.primary,
      animation: 'heartbeat 1.5s ease-in-out infinite',
      marginBottom: '0.5rem',
      textAlign: 'center' as const
    },
    { fontSize: '2rem', display: 'none' },
    { fontSize: '2.25rem', display: 'block' },
    { fontSize: '2.5rem', display: 'block' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 600,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)',
      display: 'block',
      margin: '0 auto',
      width: '100%'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem' },
    { padding: '1rem 2.5rem', fontSize: '1.1rem', width: 'auto' },
    { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      margin: '0 1rem 1.5rem 1rem',
      borderRadius: '1rem',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '2rem', margin: '0 2rem 2rem 2rem' },
    { padding: '2rem', margin: '0 2rem 2rem 2rem' }
  );

  const scoreDisplayStyle = getResponsiveStyle(
    {
      fontWeight: 800,
      color: colors.scorePrimary,
      margin: '1rem 0',
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.1)',
      animation: 'scoreReveal 0.6s ease-out'
    },
    { fontSize: '3.5rem' },
    { fontSize: '4.5rem' },
    { fontSize: '5rem' }
  );

  const scoreMessageStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: colors.resultText,
      margin: '1rem 0'
    },
    { fontSize: '1.2rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const scoreDescriptionStyle = getResponsiveStyle(
    {
      color: colors.warningText,
      lineHeight: 1.6,
      marginTop: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const namesDisplayStyle = getResponsiveStyle(
    {
      color: '#991b1b',
      marginBottom: '1.5rem',
      fontWeight: 600
    },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const disclaimerStyle = getResponsiveStyle(
    {
      background: colors.warningBg,
      borderRadius: '0.75rem',
      borderLeft: '4px solid #f59e0b'
    },
    { padding: '1.25rem', margin: '1.5rem 1rem' },
    { padding: '1.5rem', margin: '2rem' },
    { padding: '1.5rem', margin: '2rem' }
  );

  const disclaimerPStyle = getResponsiveStyle(
    {
      margin: 0,
      color: colors.warningText,
      lineHeight: 1.6
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const examplesStyle = getResponsiveStyle(
    {
      background: '#f9fafb'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const examplesH3Style = getResponsiveStyle(
    {
      color: colors.textPrimary,
      marginBottom: '1.5rem',
      textAlign: 'center' as const
    },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const examplesTableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden' as const,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const tableThStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      color: 'white',
      fontWeight: 600,
      textAlign: 'left' as const
    },
    { padding: '0.75rem 0.5rem', fontSize: '0.9rem' },
    { padding: '0.875rem 0.75rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const tableTdStyle = getResponsiveStyle(
    {
      borderBottom: '1px solid #e5e7eb',
      color: colors.textSecondary
    },
    { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
    { padding: '0.875rem 0.75rem', fontSize: '0.9rem' },
    { padding: '0.875rem 1rem', fontSize: '0.95rem' }
  );

  const scoreCellStyle = {
    fontWeight: 700,
    color: colors.scorePrimary,
    textAlign: 'center' as const
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    {
      color: colors.textSecondary,
      lineHeight: 1.8,
      paddingLeft: '1.5rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem'
    },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceStyle = {
    marginBottom: '0.5rem'
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none' as const,
    wordBreak: 'break-all' as const
  };

  const examplePairs = [
    { names: 'Joe Biden — Jill Tracy Jacobs', score: '96%' },
    { names: 'Donald Trump — Melanija Knavs', score: '12%' },
    { names: 'Bill Clinton — Monica Lewinsky', score: '90%' },
    { names: 'Romeo — Juliet', score: '100%' },
    { names: 'Dog — Cat', score: '15%' },
    { names: 'Dog — Dog', score: '96%' }
  ];

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          @keyframes scoreReveal {
            from {
              opacity: 0;
              transform: scale(0.5);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>

      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>💕 {t('toolTitle')}</h2>
            <p style={toolHeaderPStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="name1" style={labelStyle}>{t('yourName')}</label>
                <input
                  type="text"
                  id="name1"
                  placeholder={t('yourNamePlaceholder')}
                  required
                  style={inputStyle}
                  value={inputs.name1}
                  onChange={(e) => setInputs({ ...inputs, name1: e.target.value })}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <div style={heartIconStyle}>❤️</div>
              <div style={formGroupStyle}>
                <label htmlFor="name2" style={labelStyle}>{t('theirName')}</label>
                <input
                  type="text"
                  id="name2"
                  placeholder={t('theirNamePlaceholder')}
                  required
                  style={inputStyle}
                  value={inputs.name2}
                  onChange={(e) => setInputs({ ...inputs, name2: e.target.value })}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>
            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(236, 72, 153, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(236, 72, 153, 0.3)';
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <div style={namesDisplayStyle}>
                {inputs.name1} ❤️ {inputs.name2}
              </div>
              <div style={scoreDisplayStyle}>{results.score}%</div>
              <div style={scoreMessageStyle}>{results.message}</div>
              <div style={scoreDescriptionStyle}>{results.description}</div>
            </div>
          )}

          <div style={disclaimerStyle}>
            <p style={disclaimerPStyle}>
              <strong>{t('disclaimerTitle')}</strong> {t('disclaimerText')}
            </p>
          </div>

          <div style={examplesStyle}>
            <h3 style={examplesH3Style}>{t('examplesTitle')}</h3>
            <table style={examplesTableStyle}>
              <thead>
                <tr>
                  <th style={tableThStyle}>{t('examplesNames')}</th>
                  <th style={{ ...tableThStyle, textAlign: 'center' }}>{t('examplesScore')}</th>
                </tr>
              </thead>
              <tbody>
                {examplePairs.map((pair, index) => (
                  <tr
                    key={index}
                    style={{ background: 'white' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#fef3c7'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={tableTdStyle}>{pair.names}</td>
                    <td style={{ ...tableTdStyle, ...scoreCellStyle, borderBottom: index === examplePairs.length - 1 ? 'none' : '1px solid #e5e7eb' }}>
                      {pair.score}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a href="https://www.astroved.com/astropedia/en/freetools/love-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.zletai.com/storage/tools/calculations/love-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://vocal.media/journal/the-digital-cupid-understanding-love-calculators" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference3')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://timesofindia.indiatimes.com/life-style/relationships/love-sex/its-the-love-calculator/articleshow/21053440.cms" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference4')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.astrosage.com/calculators/love-calculator.asp" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference5')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.calculator.net/love-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t('reference6')}
          </a>
        </div>
      </section>
    </div>
  );
}